<template>
  <div class="nav-bar-item">
    <InputBox
      v-if="isFakeInput"
      :placeholder="popoverText"
      :focus-shortcut="shortcut"
      size="medium-tight"
      :modelValue="null"
      margin="4px 0px 4px 0px"
      prevent-focus
    >
      <template #icon><slot name="icon" /></template>
    </InputBox>
    <NavBarButton
      v-else
      :to="to"
      :exact="exact"
      :highlight-enabled="highlightEnabled"
      :data-test-id="to ? `navbar-${to.name}-link` : null"
      :font-color="fontColor"
      :is-active-forced="isActiveForced"
    >
      <component
        :is="hasTooltip ? 'ATooltip' : 'VPopover'"
        :shown="showTooltip"
        :triggers="['hover', 'focus']"
        :placement="hasTooltip ? 'right' : 'right-end'"
        :delay="{ show: 10, hide: 0 }"
        :container="false"
      >
        <div class="nav-bar-item__popover-trigger">
          <slot name="icon" />
        </div>
        <template #popper>
          <div v-if="hasTooltip" class="nav-bar-item__tooltip">
            <span
              class="nav-bar-item__tooltip-text"
              :class="{
                'nav-bar-item__tooltip-text--no-wrap': popoverText.length <= 40,
                'nav-bar-item__tooltip-text--wrap': popoverText.length > 40,
              }"
            >
              {{ popoverText }}
            </span>

            <ShortcutInfo v-if="shortcut" :shortcut="shortcut" class="nav-bar-item__shortcut-info" />
          </div>

          <slot name="popover" />
        </template>
      </component>

      <template #title>
        {{ label }}
      </template>
    </NavBarButton>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import NavBarButton from '@components/NavBar/NavBarButton.vue';
import { hasSlotContent } from '@components/slots';

/**
 * NavBarItem wraps the NavBarButton and adds an optional tooltip
 * that is only shown when the navbar is closed.
 */
export default defineComponent({
  name: 'NavBarItem',

  components: {
    NavBarButton,
  },

  props: {
    label: {
      type: String,
      required: true,
    },

    shortcut: {
      type: String,
      default: null,
    },

    to: {
      type: Object,
      default: null,
    },

    exact: {
      type: Boolean,
      default: false,
    },

    highlightEnabled: {
      type: Boolean,
      default: true,
    },

    fontColor: {
      type: String,
      default: 'default',
      validator(value) {
        return ['black', 'default'].includes(value);
      },
    },

    /**
     * If true and no `to` prop provided, the item will be highlighted if the highlightEnabled prop is true
     */
    isActiveForced: {
      type: Boolean,
      default: false,
    },

    /**
     * Tooltip text. If not set, tooltip will have set the label property as text
     */
    tooltipText: {
      type: String,
      required: false,
      default: null,
    },

    /**
     * If true, the tooltip will be shown
     */
    showTooltip: {
      type: Boolean,
      default: false,
    },

    isFakeInput: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    popoverText() {
      return this.tooltipText || this.label;
    },

    hasTooltip() {
      return !hasSlotContent(this.$slots.popover, {});
    },
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
@use 'settings/settings';
@use 'mixins/button';

.nav-bar-item {
  container-type: inline-size;
  // min-width and z-index are needed for the container query
  // to make sure the tooltip is displayed correctly
  min-width: settings.$navbar-button-width;
  z-index: settings.$navbar-item-z-index;

  @container (inline-size > 40px) {
    .tooltip {
      display: none !important;
    }
  }
}

.nav-bar-item__popover-trigger {
  @include button.navbar-button;
}

.nav-bar-item__tooltip {
  display: grid;
  grid-auto-flow: column;
  grid-gap: 8px;
  align-items: center;
  color: colors.$color-white;
  font-size: 14px;
}

.nav-bar-item__tooltip-text {
  text-align: left;

  // for longer text, we need to wrap it when it overflows
  // and set a fixed width to display the text correctly
  &--no-wrap {
    white-space: nowrap;
  }

  &--wrap {
    width: 255px;
    overflow-wrap: break-word;
    word-break: break-word;
  }
}

.nav-bar-item__shortcut-info {
  margin-left: 24px;
}
</style>
